import { useState, useEffect } from 'react';
import type { Workflow } from '@n8n-builder/n8n-types';
import { createApiClient } from '@n8n-builder/api-client';
import { API_BASE_URL } from '../../lib/api-config';
import { useSettingsStore } from '../../store/settings';
import { useCredentialsStore } from '../../store/credentials';
import { useChatStore } from '../../store/chat';
import { CheckIcon, XMarkIcon, SpinnerIcon, ChevronDownIcon } from '../ui/icons';
import { applyCredentialsToWorkflow } from '../../utils/credentials';

function humanizeType(type: string): string {
  return type
    .replace(/Api$/, ' API')
    .replace(/OAuth2$/, ' OAuth2')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/^./, (c) => c.toUpperCase());
}

interface CredentialStatusCardProps {
  workflow: Workflow;
  workflowId?: string;
  onDismiss: () => void;
}

export function CredentialStatusCard({
  workflow,
  workflowId,
  onDismiss,
}: CredentialStatusCardProps) {
  const { activeInstance } = useSettingsStore();
  const { requirements, availableCredentials, mapCredential } = useCredentialsStore();
  const { setCurrentWorkflow } = useChatStore();
  const [isApplying, setIsApplying] = useState(false);

  // Pre-select the first matching credential for each unmapped requirement
  useEffect(() => {
    for (const req of requirements) {
      if (req.mappedCredential) continue;
      const first = availableCredentials.find((c) => c.type === req.credentialType);
      if (first) {
        mapCredential(req.credentialType, { id: first.id, name: first.name });
      }
    }
  }, [requirements, availableCredentials, mapCredential]);

  if (!activeInstance) return null;
  if (requirements.length === 0) return null;

  const mappedCount = requirements.filter((r) => r.mappedCredential).length;

  const handleApply = async () => {
    setIsApplying(true);
    const updated = applyCredentialsToWorkflow(workflow, requirements);
    setCurrentWorkflow(updated);

    if (workflowId) {
      try {
        const apiClient = createApiClient(API_BASE_URL);
        await apiClient.updateWorkflow(workflowId, updated);
      } catch {
        // Silently fail — credentials are still applied in-memory
      }
    }

    useCredentialsStore.getState().setApplied(true);
    setIsApplying(false);
    onDismiss();
  };

  const handleSelect = (credentialType: string, credId: string) => {
    const cred = availableCredentials.find((c) => c.id === credId);
    if (cred) {
      mapCredential(credentialType, { id: cred.id, name: cred.name });
    }
  };

  return (
    <div className="flex justify-start animate-fade-in">
      <div className="max-w-[85%]">
        <div className="rounded-2xl border border-green-200 bg-green-50 px-4 py-3">
          <p className="text-sm font-medium text-gray-900">
            Use Pre-Existing Credentials?
          </p>
          <p className="mt-1 text-sm text-gray-600">
            Looks like you already have existing credentials set up for nodes in this workflow. Would you like to apply them?
          </p>

          {/* Credential list */}
          <div className="mt-3 space-y-2">
            {requirements.map((req) => {
              const matching = availableCredentials.filter((c) => c.type === req.credentialType);
              const isMapped = !!req.mappedCredential;

              return (
                <div
                  key={req.credentialType}
                  className="flex items-center gap-2 rounded-lg bg-white/70 border border-green-100 px-3 py-2"
                >
                  <span className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full ${isMapped ? 'bg-green-100' : 'bg-gray-100'}`}>
                    {isMapped ? (
                      <CheckIcon className="h-3 w-3 text-green-600" />
                    ) : (
                      <span className="h-2 w-2 rounded-full bg-gray-300" />
                    )}
                  </span>
                  <span className="text-sm font-medium text-gray-800 shrink-0">
                    {humanizeType(req.credentialType)}
                  </span>
                  <div className="ml-auto min-w-0">
                    {matching.length > 0 ? (
                      <div className="relative">
                        <select
                          value={req.mappedCredential?.id || ''}
                          onChange={(e) => handleSelect(req.credentialType, e.target.value)}
                          className="appearance-none rounded-md border border-gray-200 bg-white pl-2 pr-7 py-1 text-sm text-gray-700 focus:border-green-400 focus:outline-none focus:ring-1 focus:ring-green-400"
                        >
                          {matching.map((cred) => (
                            <option key={cred.id} value={cred.id}>
                              {cred.name}
                            </option>
                          ))}
                        </select>
                        <ChevronDownIcon className="pointer-events-none absolute right-1.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-gray-400" />
                      </div>
                    ) : (
                      <span className="text-xs text-gray-400 italic">No match found</span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Action buttons */}
          <div className="mt-3 flex items-center gap-2">
            <button
              onClick={handleApply}
              disabled={isApplying || mappedCount === 0}
              className="flex items-center gap-1.5 rounded-lg bg-green-600 px-3 py-1.5 text-sm font-medium text-white shadow-sm transition-colors hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isApplying ? (
                <SpinnerIcon className="h-4 w-4" />
              ) : (
                <CheckIcon className="h-4 w-4" />
              )}
              Yes
            </button>
            <button
              onClick={onDismiss}
              disabled={isApplying}
              className="flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-sm font-medium text-gray-600 shadow-sm transition-colors hover:bg-gray-50"
            >
              <XMarkIcon className="h-4 w-4" />
              No
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
